import {inject, observer} from "mobx-react";
import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid, Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
} from "@material-ui/core";
import {IManagementStore} from "Stores/Management/types";
import {Person, PersonForm, State} from "@src/Interfaces/Entity/Person";
import {IValuesStore} from "Stores/Values/types";
import {IAuthStore} from "Stores/Auth/types";
import {Values} from "Tools/Values";
import {NoContentSign} from "@src/Components/Pages/Main/Content/Persons/PersonForm/styles";
import ActionButton from "@src/Shared/Components/ActionButton";
import AddIcon from "@material-ui/icons/Add";
import {computed, observable} from "mobx";
import {Location} from "@src/Interfaces/Entity/Location";
import _ from "lodash";
import {Position} from "@src/Interfaces/Entity/Position";
import {PersonDialogDataForm} from "@src/Components/Pages/Main/Content/Persons/PersonDialog/PersonDialogDataForm/PersonDialogDataForm";
import {
    IPersonPositionItem,
    IPersonPositionItemFields, PersonPositionItem
} from "@src/Components/Pages/Main/Content/Persons/PersonDialog/PersonPositionItem/PersonPositionItem";
import {TabPanel} from "@src/Shared/Components/TabPanel";
import {
    AliasNameItem,
    IAliasNameItem,
    IAliasNameItemFields
} from "@src/Components/Pages/Main/Content/Persons/PersonDialog/AliasNameItem/AliasNameItem";
import {
    getAliasNameItemValidationSchema,
    personPositionValidationSchema,
    validateItems
} from "@src/Components/Pages/Main/Content/Persons/PersonDialog/validation";
import {AxiosCustomErrorProcessor} from "Tools/AxiosRequestsProcessor";
import {Toast} from "@src/Shared/Tools/Toast";
import { isValidPhoneNumber } from 'libphonenumber-js'

type IPersonDialogProps = {
    initialValues: Person;
    onConfirm: () => void;
    onClose: () => void;
    isOpened: boolean;
    values?: IValuesStore;
    management?: IManagementStore;
    auth?: IAuthStore;
};

function a11yProps(index: number) {
    return {
        id: `pd-tab-${index}`,
        'aria-controls': `pd-tabpanel-${index}`,
    };
}

@inject("management", "values", "auth")
@observer
export default class PersonDialog extends React.Component<IPersonDialogProps> {
    @observable selectedTab: any = 0;
    @observable personPositionItems: Array<IPersonPositionItem> = [];
    @observable aliasNameItems: Array<IAliasNameItem> = [];
    @observable lockConfirmButton: boolean = false;

    @computed get isDisabled() {
        return false;
    }

    @computed get locations(): Array<Location> {
        let items = _(this.props.management.activeLocations)
            .orderBy(x => x.name)
            .value();
        return items;
    }

    @computed get takenLocationIds(): Array<string> {
        return this.personPositionItems.map(x => x.fields.locationId);
    }

    @computed get persons(): Array<Person> {
        return this.props.management.persons;
    }

    @computed get isEdit(): boolean {
        return !!this.props.initialValues;
    }

    @computed get editIsDisabled() {
        return false;
    }

    @computed get adminPositionId() {
        return this.props.values.common["AdminPositionId"];
    }

    private listIndex: number = 0;
    private anListIndex: number = 0;
    private dataFormRef: any;
    private toast = new Toast();

    async onFormSubmit(formData: any) {
        this.lockConfirmButton = true;
        // Each alias name should be validated also for uniqueness
        const aliasValidationResult = validateItems(
            this.aliasNameItems, getAliasNameItemValidationSchema(
                this.validateFieldUniqueness.bind(this)
            )
        );
        const positionValidationResult = validateItems(this.personPositionItems, personPositionValidationSchema);
        const data: PersonForm = { ...formData };

        let switchTabAllowed = true;
        let validationPassed = true;
        if (!positionValidationResult.valid) {
            this.personPositionItems = positionValidationResult.items;
            switchTabAllowed = false;
            validationPassed = false;
        }
        if (!aliasValidationResult.valid) {
            this.aliasNameItems = aliasValidationResult.items;
            if (switchTabAllowed && this.selectedTab == 0) {
                this.selectedTab = 1;
            }
            validationPassed = false;
        }
        if (!validationPassed) {
            this.lockConfirmButton = false;
            return;
        }

        let person: Person = {
            ...data,
            state: formData.isActive ? State.Active : State.Deactivated,
            positionAssignments: this.personPositionItems.map(x => ({ locationId: x.fields.locationId, positionId: x.fields.positionId })),
            aliasNames: this.aliasNameItems.map(x => ({ name: x.fields.name, fullName: x.fields.fullName }))
        }

        const onError: AxiosCustomErrorProcessor = (err: any) => {
            const responseData = err.response.data;
            this.toast.showError(responseData.Message);
            return true;
        }

        try {
            let response;
            if (this.isEdit) {
                response = await this.props.management.updatePerson(person, onError);
            } else {
                response = await this.props.management.createPerson(person, onError);
            }
            if (response) {
                this.lockConfirmButton = false;
                this.props.onConfirm();
                return;
            }
        } catch (err) {
            console.error(err);
        }
        this.lockConfirmButton = false;
    }

    handleTabChange(ev: any, val: any) {
        this.selectedTab = val;
    }

    getPositions = (locationId: string): Position[] => {
        let location = this.locations.find(x => x.id == locationId);
        if (!location) return [];
        let items = _(location.locationPositions)
            .uniq()
            .filter(position => _.some(this.props.management.activePositions, p => p.id == position.positionId))
            .map(position => _.find(this.props.management.activePositions, p => p.id == position.positionId))
            .orderBy(x => x.name)
            .value();
        return items;
    }

    componentDidMount() {
        if (this.isEdit) {
            this.listExistingPositions();
            this.listExistingAliases();
        }
    }

    async confirmButtonClick() {
        this.dataFormRef.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    }

    closeButtonClick() {
        this.props.onClose();
    }

    listExistingPositions() {
        this.props.initialValues.positionAssignments?.forEach((x) => {
            let position: IPersonPositionItem = {
                listId: this.listIndex,
                itemId: this.listIndex.toString(),
                isNew: false,
                fields: {
                    positionId: x.positionId,
                    locationId: x.locationId,
                    locked: false
                },
                initial: {
                    positionId: x.positionId,
                    locationId: x.locationId,
                    locked: false
                }
            }
            this.listIndex += 1;
            this.personPositionItems.push(position);
        });
    }

    listExistingAliases() {
        this.props.initialValues.aliasNames?.forEach((x) => {
            let alias: IAliasNameItem = {
                listId: this.listIndex,
                itemId: this.listIndex.toString(),
                isNew: false,
                fields: {
                    name: x.name,
                    fullName: x.fullName,
                    locked: false
                },
                initial: {
                    name: x.name,
                    fullName: x.fullName,
                    locked: false
                }
            }
            this.listIndex += 1;
            this.aliasNameItems.push(alias);
        });
    }

    addNewPositionItemClick() {
        let newPosition: IPersonPositionItem = {
            listId: this.listIndex,
            itemId: null,
            isNew: true,
            fields: {
                positionId: "",
                locationId: "",
                locked: false
            }
        }
        this.listIndex += 1;
        this.personPositionItems.push(newPosition);
    }

    addNewAliasItemClick() {
        let newAlias: IAliasNameItem = {
            listId: this.listIndex,
            itemId: null,
            isNew: true,
            fields: {
                name: "",
                fullName: "",
                locked: false
            }
        }
        this.anListIndex += 1;
        this.aliasNameItems.push(newAlias);
    }


    onPositionItemChange(id: number, data: IPersonPositionItemFields) {
        this.personPositionItems.forEach((x) => {
            if (x.listId == id) {
                x.errors = {};
                x.fields = data;
            }
        });
    }

    onAliasItemChange(id: number, data: IAliasNameItemFields) {
        this.aliasNameItems.forEach((x) => {
            if (x.listId == id) {
                x.errors = {};
                x.fields = data;
            }
        });
    }

    onPositionItemRemove(id: number) {
        this.personPositionItems = this.personPositionItems.filter((x) => x.listId != id);
    }

    onAliasNameItemRemove(id: number) {
        this.aliasNameItems = this.aliasNameItems.filter((x) => x.listId != id);
    }

    validateFieldUniqueness(fieldName: string) {
        const persons = this.persons;
        const parentId = this.props.initialValues?.id;
        return function (value: string) {
            if (!value) { return true; }
            let filteredPersons: any = [];
            if (fieldName === "email") { filteredPersons = persons.filter((person) => person.email.toLowerCase() === value.toLowerCase()); }
            if (fieldName === "name") {
                filteredPersons = persons.filter(
                    (person) =>
                        (person.name.toLowerCase() === value.toLowerCase() && person.id !== (parentId)) ||
                        person.aliasNames.find((alias) => alias.name.toLowerCase() === value.toLowerCase())
                );
            }
            if (filteredPersons.length === 0) { return true; }
            return parentId !== "00000000-0000-0000-0000-000000000000" &&
                filteredPersons[0].id === parentId;
            // && fieldName !== "name";
        };
    }

    validatePhoneField() {
        return function (value: string) {
            if (value == null) {
                return true;
            } else {
                return isValidPhoneNumber(value);
            }
        }
    }

    render() {
        return (
            <Dialog
                maxWidth={"lg"}
                open={this.props.isOpened}
                onClose={this.props.onClose}
                aria-labelledby="person-dialog-title"
            >
                <DialogTitle id="person-dialog-title">
                    {this.isEdit ? Values.getLocalizationValue("editPerson") : Values.getLocalizationValue("addNewPerson")}
                </DialogTitle>
                <DialogContent style={{ minWidth: 1000, paddingBottom: 32 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={5}>
                            <PersonDialogDataForm
                                setRef={ref => this.dataFormRef = ref}
                                onSubmit={data => this.onFormSubmit(data)}
                                activeOrganisations={this.props.management.activeOrganisations}
                                persons={this.persons}
                                uniquenessValidator={this.validateFieldUniqueness.bind(this)}
                                phoneValidator={this.validatePhoneField.bind(this)}
                                initialValues={this.props.initialValues}
                                isDisabled={this.editIsDisabled}
                            />
                        </Grid>
                        <Grid item xs={7} style={{ borderLeft: "1px solid #ececec", paddingTop: 0 }}>
                            <Tabs
                                value={this.selectedTab}
                                onChange={(ev: any, val: any) => this.selectedTab = val}
                                indicatorColor={"primary"}
                            >
                                <Tab label={Values.getLocalizationValue("positions")} {...a11yProps(0)} />
                                <Tab label={Values.getLocalizationValue("aliasNames")} {...a11yProps(1)} />
                            </Tabs>
                            <TabPanel value={this.selectedTab} index={0}>
                                <TableContainer style={{ marginTop: 8 }}>
                                    <Table>
                                        <TableHead style={{ backgroundColor: "#f9f9f9" }}>
                                            <TableRow>
                                                <TableCell>{Values.getLocalizationValue("location")}</TableCell>
                                                <TableCell>{Values.getLocalizationValue("position")}</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            { this.personPositionItems.map((pp: IPersonPositionItem, i: number) => (
                                                <PersonPositionItem
                                                    key={i}
                                                    item={pp}
                                                    onChange={(id: any, data: IPersonPositionItemFields) => { this.onPositionItemChange(id, data) }}
                                                    onRemove={(id: any) => { this.onPositionItemRemove(id) }}
                                                    locations={this.locations}
                                                    positions={this.getPositions(pp.fields.locationId)}
                                                    takenLocationIds={this.takenLocationIds}
                                                    lockInputs={this.editIsDisabled}
                                                    adminPositionId={this.adminPositionId}
                                                    enableAdminSelection={this.props.auth.isAdmin}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                { this.personPositionItems.length == 0 && (
                                    <NoContentSign>{Values.getLocalizationValue("listOfPositionsIsEmpty")}</NoContentSign>
                                )}
                                <div>
                                    <ActionButton
                                        disabled={this.takenLocationIds?.length == this.locations?.length || this.editIsDisabled}
                                        onClick={() => this.addNewPositionItemClick()}
                                        icon={<AddIcon />}
                                        label={Values.getLocalizationValue("addPosition")}
                                        color={"maranics"}
                                        labelColor={"maranics"}
                                    />
                                </div>
                            </TabPanel>
                            <TabPanel value={this.selectedTab} index={1}>
                                <TableContainer style={{ marginTop: 8 }}>
                                    <Table>
                                        <TableHead style={{ backgroundColor: "#f9f9f9" }}>
                                            <TableRow>
                                                <TableCell>{Values.getLocalizationValue("name")}</TableCell>
                                                <TableCell>{Values.getLocalizationValue("fullName")}</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            { this.aliasNameItems.map((an: IAliasNameItem, i: number) => (
                                                <AliasNameItem
                                                    key={i}
                                                    item={an}
                                                    onChange={(id: any, data: IAliasNameItemFields) => { this.onAliasItemChange(id, data) }}
                                                    onRemove={(id: any) => { this.onAliasNameItemRemove(id) }}
                                                    lockInputs={this.editIsDisabled}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                { this.personPositionItems.length == 0 && (
                                    <NoContentSign>{Values.getLocalizationValue("listOfAliasesIsEmpty")}</NoContentSign>
                                )}
                                <div>
                                    <ActionButton
                                        disabled={this.editIsDisabled}
                                        onClick={() => this.addNewAliasItemClick()}
                                        icon={<AddIcon />}
                                        label={Values.getLocalizationValue("addAliasName")}
                                        color={"maranics"}
                                        labelColor={"maranics"}
                                    />
                                </div>
                            </TabPanel>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: 16 }}>
                    <Button variant={"outlined"} onClick={() => this.props.onClose()} color="default">
                        {Values.getLocalizationValue("cancel")}
                    </Button>
                    <Button variant={"contained"} onClick={() => this.confirmButtonClick()} color="primary" disabled={this.lockConfirmButton}>
                        {Values.getLocalizationValue("confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }
}